import { useMemo } from 'react';
import { getCommaSeparatedUIDs } from '@utils/textHelper';
import { getBatchSubscriptionByBatchUID } from '@models/Me';
import { getRelativeDays } from '@utils/dates';
import { subscriptionTypes } from '@constants/index';
import usePlatformInfo from '@hooks/usePlatformInfo';
import isEmpty from 'lodash/isEmpty';
import { getMe } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  getTotalActiveBatches,
  getBatchGroupSubscriptions,
  hasActiveBatch
} from '@api/selectors/subscription';
import { getIsRegistered } from '@stores/selectors/login';
import useLoginStore from '@stores/login/loginStore';

const getBatchState = (startDate, completedDate) => {
  if (!startDate || !completedDate) return 'NA';
  const currentDate = new Date();
  const d1 = new Date(startDate);
  const d2 = new Date(completedDate);
  if (currentDate < d1) return 'Upcoming';
  if (currentDate > d2) return 'Finished';
  return 'Ongoing';
};

const usePlatformEventProps = ({
  batch = {},
  isBatch = false,
  entityContentType = false,
  lastPrimarySourceSection = 'NA',
  lastPrimarySource = null,
  extraProps = {},
  isPurchaseType = false,
  isSlashedPriceType = false
}) => {
  const me = useMyInfo(getMe);
  const isRegistered = useLoginStore(getIsRegistered);
  const batchGroupSubscriptions = getBatchGroupSubscriptions(me.purchasedItems);
  const activeBatchSubscriptions = getTotalActiveBatches(me.purchasedItems);
  const batchSubscriptionActive = hasActiveBatch(me.purchasedItems);
  const info = usePlatformInfo(batch);
  return useMemo(() => {
    const {
      startsAt,
      authors,
      completedAt,
      isEnrolled,
      languages,
      goal,
      lessonsCount: sessions,
      enrollmentEndDate,
      batchExpiryDate,
      trialClasses,
      discountEndDate
    } = batch || {};
    const { uid: goalUID, name: goalName } = goal || {};
    const subscription =
      getBatchSubscriptionByBatchUID(batchGroupSubscriptions, batch?.uid) ?? {};
    const { user_subscription_timeline = [{}] } = subscription;
    const {
      time_remaining = 0,
      expired_at = 0,
      duration = 0,
      subscription_type,
      started_at: purchasedDate = 0
    } = user_subscription_timeline[0] || {};
    const getSubscriptionType = (type) => {
      const entries = Object.entries(subscriptionTypes);
      const subscribedType = entries.find((entry) => entry[1] === type);
      return subscribedType ? subscribedType[0] : 'NA';
    };
    const is_subscription_active = time_remaining > 0;
    const hasBatchSubscription = !!subscription.user_subscription_timeline;
    // eslint-disable-next-line no-nested-ternary
    const purchaseType = hasBatchSubscription
      ? hasBatchSubscription.time_remaining
        ? 'extension'
        : 'renewal'
      : 'new';

    const segmentData = {
      entity_uid: batch?.uid,
      entity_title: batch?.name,
      start_date: startsAt,
      end_date: completedAt,
      batch_expiry_date: batchExpiryDate || 'NA',
      educator_uid: getCommaSeparatedUIDs(authors) || 'NA',
      educator_uid_list: getCommaSeparatedUIDs(authors) || 'NA',
      educator_name: getCommaSeparatedUIDs(authors, 'name') || 'NA',
      educator_username: getCommaSeparatedUIDs(authors, 'username') || 'NA',
      educator_username_list:
        getCommaSeparatedUIDs(authors, 'username') || 'NA',
      educator_name_list: getCommaSeparatedUIDs(authors, 'name') || 'NA',
      number_of_sessions: sessions || 'NA',
      days_till_batch_end: getRelativeDays(true, completedAt),
      days_till_enrollment_end: getRelativeDays(true, enrollmentEndDate),
      number_of_active_batch_subscription: activeBatchSubscriptions,
      is_enrolled: isEnrolled || false,
      purchase_enrollment_end_datediff: getRelativeDays(
        false,
        enrollmentEndDate,
        purchasedDate
      ),
      language: getCommaSeparatedUIDs(languages, 'label') || 'NA',
      goal_name: goalName,
      goal_uid: goalUID,
      is_subscription_active,
      is_batch_subscription_active: batchSubscriptionActive,
      last_primary_source_section: lastPrimarySourceSection,
      subscription_duration: duration || 'NA',
      subscription_end_date: expired_at || 'NA',
      user_subscription_type: getSubscriptionType(subscription_type),
      has_trial_classes: !isEmpty(trialClasses),
      is_new_user: !isRegistered,
      is_upgrade: 'NA',
      ...extraProps
    };
    if (isBatch) {
      segmentData.subscription_price = batch?.price || 'NA';
      segmentData.batch_title = batch?.name;
      segmentData.batch_state = getBatchState(startsAt, completedAt);
      segmentData.batch_uid = batch?.batchUID;
    }
    if (entityContentType) {
      segmentData.entity_content_type = info?.entityContentType;
    }
    if (isPurchaseType) {
      segmentData.purchase_type = purchaseType;
    }
    if (lastPrimarySource) {
      segmentData.last_primary_source = lastPrimarySource;
    }
    if (isSlashedPriceType) {
      segmentData.is_price_slashed = batch?.isDiscountAvailable ?? false;
      segmentData.num_days_to_price_increase = getRelativeDays(
        true,
        discountEndDate
      );
      segmentData.listing_price = batch?.listPrice || 'NA';
    }
    return segmentData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, batchSubscriptionActive]);
};

export default usePlatformEventProps;
